import {
  ApartmentOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  RetweetOutlined,
  SettingOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Divider,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Table,
  Tag,
  Tour,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GondolaIcon from "../../components/GondolaIcon";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import useRevenue from "../../utils/useRevenue";
import { mensagemDeErro } from "../collections-change/promoHelpers";
import EscopoDoEvento from "./EscopoDoEvento";
import ManualSkuAssignment from "./ManualSkuAssignment";
import TimelineSegmentActions, {
  sortTimelineRows,
} from "./TimelineSegmentActions";
import {
  ESCOPO_VAZIO,
  erroDoEscopo,
  escopoEditavel,
  estadoDoEscopo,
  montarEscopos,
  resumoDoEscopo,
} from "./escopoHelpers";

const { Text } = Typography;
const { TextArea } = Input;

const NAIVE_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const ZONE_COLLECTION_HISTORY_PAGE_SIZE = 20;

const BASE_CARD_OPTIONS = [
  {
    value: "new_event",
    label: "Registrar/Editar eventos",
    icon: <PlusOutlined />,
  },
  {
    value: "edit_zone_collection",
    label: "Gerenciar zonas e categorias",
    // A tela liga zona a categoria: é relação, não configuração. A engrenagem
    // não dizia nada e ainda repetia o botão ao lado.
    icon: <ApartmentOutlined />,
  },
  {
    value: "edit_zone_module",
    label: "Gerenciar zonas e módulos",
    icon: <GondolaIcon />,
  },
];

const SUPERADMIN_CARD_OPTIONS = [
  {
    value: "manual_sku_assignment",
    // O rótulo ecoa o título da própria tela, "Estrutura de categorias por
    // SKU". "Atribuição manual" nomeava o mecanismo que está sendo aposentado,
    // e a tela hoje mostra a categoria efetiva de cada SKU, não uma atribuição.
    label: "Categorias por SKU",
    // RetweetOutlined são duas setas em ciclo: lê como sincronizar. Categorizar
    // é etiquetar.
    icon: <TagsOutlined />,
    superadminOnly: true,
  },
];

export default function CollectionsManager() {
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedOption, setSelectedOption] = useState("");

  // Adicionar uma nova categoria
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [isOuranZoneMode, setIsOuranZoneMode] = useState(false);

  // Registrar um novo evento
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventComment, setNewEventComment] = useState("");
  const [newEventEndDate, setNewEventEndDate] = useState(null);
  const [zoneAffected, setZoneAffected] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [allBranchesForSelector, setAllBranchesForSelector] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);

  // Editar evento existente
  const [editingEventGroupId, setEditingEventGroupId] = useState(null);
  const [editingEventIds, setEditingEventIds] = useState([]);
  const [editingIsMultiBranch, setEditingIsMultiBranch] = useState(false);
  const [editingBranchCount, setEditingBranchCount] = useState(1);
  const [previousEvents, setPreviousEvents] = useState([]);
  const [eventsFilterStart, setEventsFilterStart] = useState(null);
  const [eventsFilterEnd, setEventsFilterEnd] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [escopoDoEvento, setEscopoDoEvento] = useState(ESCOPO_VAZIO);
  const [editingEvento, setEditingEvento] = useState(null);

  // Adicionar uma Categoria a uma zona
  const [zoneCollection, setZoneCollection] = useState([{}]);
  const [zonesList, setZonesList] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [isAddingCollection, setIsAddingCollection] = useState(false);
  const [inactiveCollectionsVisibleCount, setInactiveCollectionsVisibleCount] =
    useState(ZONE_COLLECTION_HISTORY_PAGE_SIZE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatedZoneCollectionFlag, setUpdateZoneCollectionFlag] =
    useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [newCollectionStartDate, setNewCollectionStartDate] = useState(null);

  // Substituir a categoria de uma zona
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [collectionToReplace, setCollectionToReplace] = useState(null);
  const [selectedNewCollectionForReplace, setSelectedNewCollectionForReplace] =
    useState(null);

  // Remover a categoria de uma zona
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [collectionToRemove, setCollectionToRemove] = useState(null);
  const [selectedRemoveEndDate, setSelectedRemoveEndDate] = useState(null);

  const [datePickerNow, setDatePickerNow] = useState(
    dayjs(new Date().toISOString())
  );
  const [selectedDate, setSelectedDate] = useState("");

  // Gerenciar zonas e módulos (Jasper)
  const [zoneModuleId, setZoneModuleId] = useState("");
  const [zoneModuleLinks, setZoneModuleLinks] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [newModuleStartDate, setNewModuleStartDate] = useState(null);
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [isLoadingModules, setIsLoadingModules] = useState(false);

  const {
    postEvent,
    patchEvent,
    updateEventGroup,
    deleteEventGroup,
    getEventsList,
    postCollection,
    getZoneActualCollection,
    getZoneCollectionsHistory,
    patchZoneCollectionUnion,
    patchZoneCollectionSegment,
    replaceZoneCollection,
    deleteZoneCollectionSegment,
    postZonesCollections,
    getZones,
    getCollections,
    getBranches,
    getClients,
    getJasperModules,
    getZonesModules,
    postZonesModules,
    closeZonesModules,
    patchZonesModuleSegment,
    deleteZonesModule,
    getOuranCatalogPilot,
    getOuranZoneCategories,
    getOuranZoneCategoryHistory,
    createOuranZoneCategory,
    closeOuranZoneCategory,
    replaceOuranZoneCategory,
    editOuranZoneCategorySegment,
    deleteOuranZoneCategorySegment,
  } = useApi();

  const { getRevenueCollections } = useRevenue();

  const { shouldShowTutorialBool, postShouldNeverShowTutorial, user } =
    useAuth();
  const isPrivileged = user?.is_superuser || user?.is_client_admin;
  const editandoEventoDoErp = editingEvento?.origem === "erp";
  const escopoTravado =
    Boolean(editingEvento) && !escopoEditavel(editingEvento);
  const escopoPorProdutoPermitido =
    !editingIsMultiBranch &&
    (selectedBranches.length === 0 ||
      (selectedBranches.length === 1 &&
        selectedBranches[0] === user?.active_branch_id));
  const tipoDoEscopo = escopoPorProdutoPermitido ? escopoDoEvento.tipo : "zona";
  const [isSeventhPhaseTutorialOpen, setIsSeventhPhaseTutorialOpen] =
    useState(true);
  const [isEighthPhaseTutorialOpen, setIsEighthPhaseTutorialOpen] =
    useState(false);
  const [isNinthPhaseTutorialOpen, setIsNinthPhaseTutorialOpen] =
    useState(false);
  const [isFinalTutorialModalOpen, setIsFinalTutorialModalOpen] =
    useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const pilot = await getOuranCatalogPilot();
        const isOuran = pilot.category_mode === "ouran";
        setIsOuranZoneMode(isOuran);
        const options = isOuran
          ? (await getOuranZoneCategories()).categories.map((category) => ({
              value: category.id,
              label: category.name,
              scope: category.scope,
            }))
          : (await getRevenueCollections()).data.collections.map(
              (collection) => ({
                value: collection.col_sequencial,
                label: collection.col_descricao,
              })
            );
        setCollections(options);
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
        // Instâncias sem o piloto Ouran habilitado continuam no contrato ERP;
        // não deixar uma indisponibilidade da rota nova esvaziar essa tela.
        setIsOuranZoneMode(false);
        try {
          const response = await getRevenueCollections();
          setCollections(
            response.data.collections.map((collection) => ({
              value: collection.col_sequencial,
              label: collection.col_descricao,
            }))
          );
        } catch (legacyError) {
          console.error("Erro ao carregar categorias ERP:", legacyError);
          setCollections([]);
        }
      }
    };

    loadCollections();
    // useApi/useRevenue devolvem funções novas a cada render; depender delas
    // aqui criaria um ciclo de recarga. A troca de filial é o gatilho real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.active_branch_id]);

  useEffect(() => {
    if (selectedOption === "new_event" && isPrivileged) {
      Promise.all([getClients(), getBranches()]).then(([cRes, bRes]) => {
        const clients = cRes?.data || cRes || [];
        const branches = bRes?.data || bRes || [];

        setAvailableClients(
          clients.map((c) => ({ value: c.id, label: c.name }))
        );
        setAllBranchesForSelector(branches);

        // Pre-select the company of the current active branch
        const activeBranchId = user?.active_branch_id;
        const activeBranch = branches.find((b) => b.id === activeBranchId);
        const activeClientId = activeBranch?.client ?? null;

        setSelectedCompany(activeClientId);
        setAvailableBranches(
          branches
            .filter((b) => b.client === activeClientId)
            .map((b) => ({ value: b.id, label: b.name }))
        );
      });
    }
  }, [selectedOption, isPrivileged]);

  const handleCompanyChange = (clientId) => {
    setSelectedCompany(clientId);
    setSelectedBranches([]);
    const filtered = allBranchesForSelector
      .filter((b) => b.client === clientId)
      .map((b) => ({ value: b.id, label: b.name }));
    setAvailableBranches(filtered);
  };

  const selectAllBranches = () => {
    const allIds = availableBranches.map((b) => b.value);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedBranches.includes(id));
    setSelectedBranches(allSelected ? [] : allIds);
  };

  const feedbackMessage = (type, text) => {
    messageApi.open({
      type: type,
      content: text,
    });
  };

  // Registrar um novo evento
  const createEvent = () => {
    const isMultiBranch = selectedBranches.length > 1;
    const porProduto = !isMultiBranch && tipoDoEscopo !== "zona";

    if (newEventTitle === "" || newEventComment === "") {
      feedbackMessage("error", "Preencha todos os campos!");
      return;
    }
    if (
      !isMultiBranch &&
      !porProduto &&
      (!Array.isArray(zoneAffected) || zoneAffected.length === 0)
    ) {
      feedbackMessage("error", "Preencha todos os campos!");
      return;
    }
    const erroDeEscopo = porProduto ? erroDoEscopo(escopoDoEvento) : null;
    if (erroDeEscopo) {
      feedbackMessage("error", erroDeEscopo);
      return;
    }

    const base = {
      datetime: selectedDate ? selectedDate : dayjs().format("YYYY-MM-DD"),
      end_datetime: newEventEndDate
        ? newEventEndDate.format("YYYY-MM-DD")
        : null,
      title: newEventTitle,
      comment: newEventComment,
    };

    const params = isMultiBranch
      ? { ...base, branch_ids: selectedBranches }
      : porProduto
        ? {
            ...base,
            scopes: montarEscopos(escopoDoEvento),
            mechanic: escopoDoEvento.mecanica,
          }
        : { ...base, zone: zoneAffected };

    postEvent(params)
      .then((response) => {
        const skipped = response?.data?.skipped_branches;
        if (skipped && skipped.length > 0) {
          feedbackMessage(
            "warning",
            `Evento criado, mas ${skipped.length} filial(is) foram ignoradas por não terem zonas ativas.`
          );
        } else {
          feedbackMessage("success", "Evento registrado com sucesso!");
        }
        setZoneAffected([]);
        setSelectedBranches([]);
        setSelectedCompany(null);
        setNewEventTitle("");
        setNewEventComment("");
        setNewEventEndDate(null);
        setEscopoDoEvento(ESCOPO_VAZIO);
        loadPreviousEvents();
      })
      .catch((error) => {
        feedbackMessage(
          "error",
          porProduto
            ? mensagemDeErro(error, "Algo deu errado!")
            : `Algo deu errado!: ${error}`
        );
      });
  };

  // Salvar edição de evento existente
  const saveEditEvent = () => {
    if (editandoEventoDoErp) {
      if (newEventTitle.trim() === "") {
        feedbackMessage("error", "Preencha o título!");
        return;
      }
      patchEvent(editingEventIds[0], {
        title: newEventTitle,
        comment: newEventComment,
      })
        .then(() => {
          feedbackMessage("success", "Evento atualizado com sucesso!");
          cancelEditMode();
          loadPreviousEvents();
        })
        .catch((error) => {
          feedbackMessage("error", mensagemDeErro(error, "Algo deu errado!"));
        });
      return;
    }
    const porProduto = !editingIsMultiBranch && tipoDoEscopo !== "zona";
    if (editingIsMultiBranch && selectedBranches.length === 0) {
      feedbackMessage("error", "Selecione ao menos uma filial!");
      return;
    }
    const zoneValid =
      editingIsMultiBranch ||
      porProduto ||
      (Array.isArray(zoneAffected) && zoneAffected.length > 0);
    if (!zoneValid || newEventTitle === "" || newEventComment === "") {
      feedbackMessage("error", "Preencha todos os campos!");
      return;
    }
    const erroDeEscopo =
      porProduto && !escopoTravado ? erroDoEscopo(escopoDoEvento) : null;
    if (erroDeEscopo) {
      feedbackMessage("error", erroDeEscopo);
      return;
    }

    const base = {
      datetime: selectedDate ? selectedDate : dayjs().format("YYYY-MM-DD"),
      end_datetime: newEventEndDate
        ? newEventEndDate.format("YYYY-MM-DD")
        : null,
      title: newEventTitle,
      comment: newEventComment,
    };

    const params = editingIsMultiBranch
      ? { ...base, branch_ids: selectedBranches }
      : !porProduto
        ? { ...base, zone: zoneAffected }
        : escopoTravado
          ? base
          : {
              ...base,
              scopes: montarEscopos(escopoDoEvento),
              mechanic: escopoDoEvento.mecanica,
            };

    updateEventGroup(editingEventIds, params)
      .then(() => {
        feedbackMessage("success", "Evento atualizado com sucesso!");
        cancelEditMode();
        loadPreviousEvents();
      })
      .catch((error) => {
        feedbackMessage(
          "error",
          porProduto
            ? mensagemDeErro(error, "Algo deu errado!")
            : `Algo deu errado!: ${error}`
        );
      });
  };

  const cancelEditMode = () => {
    setEditingEventGroupId(null);
    setEditingEvento(null);
    setEscopoDoEvento(ESCOPO_VAZIO);
    setEditingEventIds([]);
    setEditingIsMultiBranch(false);
    setEditingBranchCount(1);
    setZoneAffected([]);
    setNewEventTitle("");
    setNewEventComment("");
    setNewEventEndDate(null);
    setSelectedDate("");
    setDatePickerNow(dayjs());
    setSelectedBranches([]);
    const activeBranchId = user?.active_branch_id;
    const activeBranch = allBranchesForSelector.find(
      (b) => b.id === activeBranchId
    );
    const activeClientId = activeBranch?.client ?? null;
    setSelectedCompany(activeClientId);
    setAvailableBranches(
      allBranchesForSelector
        .filter((b) => b.client === activeClientId)
        .map((b) => ({ value: b.id, label: b.name }))
    );
  };

  const handleDeleteEvent = async (eventGroupId, eventIds) => {
    try {
      setDeletingEventId(eventGroupId);
      await deleteEventGroup(eventIds);
      if (editingEventGroupId === eventGroupId) {
        cancelEditMode();
      }
      feedbackMessage("success", "Evento exclu\u00EDdo com sucesso!");
      loadPreviousEvents();
    } catch (error) {
      feedbackMessage("error", "Algo deu errado ao excluir o evento!");
      console.error(error);
    } finally {
      setDeletingEventId(null);
    }
  };

  const selectAllEventZones = () => {
    const allZoneIds = zonesList.map((zone) => zone.value);
    const allSelected =
      allZoneIds.length > 0 &&
      allZoneIds.every((zoneId) => zoneAffected.includes(zoneId));

    setZoneAffected(allSelected ? [] : allZoneIds);
  };

  // Carregar lista de eventos anteriores
  const loadPreviousEvents = async () => {
    setIsLoadingEvents(true);
    try {
      const params = {};
      if (eventsFilterStart)
        params.start_date = eventsFilterStart.format("YYYY-MM-DD");
      if (eventsFilterEnd)
        params.end_date = eventsFilterEnd.format("YYYY-MM-DD");
      const response = await getEventsList(params);
      setPreviousEvents(response.data);
    } catch (error) {
      console.error("Erro ao carregar eventos:", error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const selectEventForEdit = (event) => {
    setEditingEventGroupId(event.id);
    setEditingEvento(event);
    setEscopoDoEvento(estadoDoEscopo(event));
    if (!event.is_multi_branch) setSelectedBranches([]);
    setEditingEventIds(event.event_ids || []);
    setEditingIsMultiBranch(event.is_multi_branch || false);
    setEditingBranchCount(event.branch_count || 1);
    setZoneAffected(event.zone_ids || []);
    setNewEventTitle(event.title);
    setNewEventComment(event.comment);
    const eventDate = dayjs(event.datetime.slice(0, 10));
    setSelectedDate(eventDate.format("YYYY-MM-DD"));
    setDatePickerNow(eventDate);
    setNewEventEndDate(
      event.end_datetime ? dayjs(event.end_datetime.slice(0, 10)) : null
    );

    // For multi-branch events, pre-populate company and branch selectors
    if (
      event.is_multi_branch &&
      event.branch_ids &&
      event.branch_ids.length > 0
    ) {
      const branchIds = event.branch_ids;
      setSelectedBranches(branchIds);
      const firstBranch = allBranchesForSelector.find((b) =>
        branchIds.includes(b.id)
      );
      if (firstBranch) {
        const clientId = firstBranch.client;
        setSelectedCompany(clientId);
        setAvailableBranches(
          allBranchesForSelector
            .filter((b) => b.client === clientId)
            .map((b) => ({ value: b.id, label: b.name }))
        );
      }
    }
  };

  // Obter dados da zona selecionada

  function getZoneCollection(zone) {
    const request = isOuranZoneMode
      ? getOuranZoneCategoryHistory(zone)
      : getZoneCollectionsHistory(zone).then((response) => response.data);
    request
      .then((rows) => {
        setZoneCollection(sortTimelineRows(rows));
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          feedbackMessage("warning", "Zona não possui categorias cadastradas!");
          setZoneCollection([]);
          return;
        }
        feedbackMessage("error", "Algo deu errado!");
      });
  }

  // Funções auxiliares para gerenciar Zonas e Categorias

  const performRemoveCollection = async (zoneId, collection, endTime) => {
    if (isOuranZoneMode) {
      return closeOuranZoneCategory(collection.zone_ouran_category_id, {
        union_end_datetime: endTime,
      });
    }
    const params2 = {
      union_end_datetime: endTime,
      collection: collection.legacy_id,
    };
    await patchZoneCollectionUnion(zoneId, params2);
  };

  const performRegisterCollection = async (
    zoneId,
    legacyId,
    description,
    startDateTime
  ) => {
    if (isOuranZoneMode) {
      const created = await createOuranZoneCategory({
        zone_id: zoneId,
        category_id: legacyId,
        union_start_datetime:
          startDateTime || dayjs().startOf("day").format(NAIVE_DATETIME_FORMAT),
      });
      return created.zone_ouran_category_id;
    }
    const params = { description, legacy_id: legacyId };
    await postCollection(params);

    const zonesParams = {
      zone: zoneId,
      collection: legacyId,
      union_start_datetime:
        startDateTime || dayjs().startOf("day").format(NAIVE_DATETIME_FORMAT),
    };
    const created = await postZonesCollections(zonesParams);

    const createdId =
      created?.data?.zonecollection_id ??
      created?.data?.zonescollections_id ??
      created?.data?.id ??
      null;

    if (createdId) return createdId;

    const resp = await getZoneActualCollection(zoneId);
    const active = (resp?.data || []).find(
      (col) => col.union_end_datetime === null && col.legacy_id === legacyId
    );
    return active?.zonecollection_id ?? null;
  };

  // Remover uma categoria

  const removeCollection = async (collection, endDate) => {
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    const unionEndTime = endDate
      ? endDate.format(NAIVE_DATETIME_FORMAT)
      : dayjs().endOf("day").format(NAIVE_DATETIME_FORMAT);

    try {
      await performRemoveCollection(zoneId, collection, unionEndTime);
      setUpdateZoneCollectionFlag((prev) => !prev);
      feedbackMessage(
        "success",
        `Categoria ${collectionToRemove.collection} removida da zona ${zoneName} com sucesso!`
      );
    } catch (error) {
      feedbackMessage("error", "Algo deu errado ao remover a categoria!");
      console.error(error);
    }
  };

  // Registar o vínculo entre uma zona e categoria e criar a categoria na DataBase da aplicação

  const registerCollectionAndZone = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    try {
      await performRegisterCollection(
        zoneId,
        selectedCollection.value,
        selectedCollection.label,
        newCollectionStartDate.format(NAIVE_DATETIME_FORMAT)
      );
      feedbackMessage(
        "success",
        `Nova categoria adicionada e associada à zona ${zoneName} com sucesso.`
      );

      setUpdateZoneCollectionFlag((prev) => !prev);
      setSelectedCollection(null);
      setNewCollectionStartDate(null);
    } catch (error) {
      feedbackMessage(
        "error",
        mensagemDeErro(error, "Não foi possível vincular a categoria.")
      );
    } finally {
      setIsSubmitting(false);
    }
    setIsFinalTutorialModalOpen(true);
  };

  // Substituir a categoria de uma zona.
  // -> Encerra o relacionamento atual e cria um novo

  const replaceFirstAllowedDay = collectionToReplace?.union_start_datetime
    ? dayjs(collectionToReplace.union_start_datetime).startOf("day")
    : null;
  const replaceLastDay = (() => {
    const chosen = selectedEndDate
      ? dayjs(selectedEndDate)
      : dayjs().startOf("day");
    return replaceFirstAllowedDay && chosen.isBefore(replaceFirstAllowedDay)
      ? replaceFirstAllowedDay
      : chosen;
  })();

  const updateCollection = async (oldCollection, newCollectionObj) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const zoneName =
      zonesList.find((zone) => zone.value === zoneId)?.label || zoneId;

    const lastDay = replaceLastDay.format("YYYY-MM-DD");

    try {
      // 1) Encerrar relação antiga
      if (isOuranZoneMode) {
        await replaceOuranZoneCategory(oldCollection.zone_ouran_category_id, {
          category_id: newCollectionObj.value,
          last_day: lastDay,
        });
      } else {
        await replaceZoneCollection(zoneId, {
          old_zonecollection_id: oldCollection.zonecollection_id,
          new_legacy_id: newCollectionObj.value,
          last_day: lastDay,
        });
      }

      feedbackMessage(
        "success",
        `Relação substituída com sucesso! ${zoneName} agora possui a nova categoria (${newCollectionObj.label}).`
      );
      setUpdateZoneCollectionFlag((prev) => !prev);
      setShowReplaceModal(false);
      setSelectedNewCollectionForReplace(null);
      setSelectedEndDate(null);
    } catch (error) {
      feedbackMessage(
        "error",
        mensagemDeErro(error, "Não foi possível substituir a categoria.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // useEffect para atualizar o card

  useEffect(() => {
    if (zoneId) {
      getZoneCollection(zoneId);
    }
  }, [updatedZoneCollectionFlag, zoneId]);

  // Limpar os estados utilizados
  const cleanUsesState = () => {
    setZoneAffected([]);
    setNewEventTitle("");
    setNewEventComment("");
    setNewEventEndDate(null);
    setEditingEventGroupId(null);
    setEditingEventIds([]);

    setZoneCollection([]);
    setZoneId("");
  };

  const handleFinalTutorial = () => {
    setIsFinalTutorialModalOpen(false);
    postShouldNeverShowTutorial();
  };

  // Load ZonesModules links for the selected zone
  const loadZoneModuleLinks = async (zId) => {
    try {
      const response = await getZonesModules(zId);
      setZoneModuleLinks(sortTimelineRows(response.data || []));
    } catch {
      setZoneModuleLinks([]);
    }
  };

  // Load Jasper modules for the current branch
  const loadModulesForBranch = async (branchId) => {
    if (!branchId) {
      setModulesList([]);
      return;
    }
    setIsLoadingModules(true);
    try {
      const response = await getJasperModules(branchId);
      const modules = (response.data || []).map((m) => ({
        value: m.id,
        label: m.gondola ? `${m.code} (${m.gondola})` : m.code,
        categories: m.categories || [],
      }));
      setModulesList(modules);
    } catch {
      setModulesList([]);
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleAddModule = async () => {
    if (!zoneModuleId || !selectedModuleId) return;
    try {
      await postZonesModules({
        zoneId: zoneModuleId,
        moduleId: selectedModuleId,
        unionStartDatetime: newModuleStartDate
          ? newModuleStartDate.format(NAIVE_DATETIME_FORMAT)
          : dayjs().format(NAIVE_DATETIME_FORMAT),
      });
      setIsAddingModule(false);
      setSelectedModuleId(null);
      setNewModuleStartDate(null);
      await loadZoneModuleLinks(zoneModuleId);
      messageApi.success("Módulo vinculado com sucesso!");
    } catch (error) {
      messageApi.error(mensagemDeErro(error, "Erro ao vincular módulo."));
    }
  };

  const handleCloseModuleLink = async (linkId) => {
    try {
      await closeZonesModules(linkId);
      await loadZoneModuleLinks(zoneModuleId);
      messageApi.success("Relação encerrada.");
    } catch {
      messageApi.error("Erro ao encerrar relação.");
    }
  };

  const targetZoneBranchId =
    isPrivileged && selectedBranches.length === 1 ? selectedBranches[0] : null;

  useEffect(() => {
    const loadZones = async () => {
      try {
        const zonesResponse = await getZones(true, targetZoneBranchId);
        const zonesData = zonesResponse.data || [];
        const zonesDict = zonesData
          .map((zone) => ({ value: zone.id, label: zone.description }))
          .sort((a, b) =>
            String(a.label || "").localeCompare(
              String(b.label || ""),
              "pt-BR",
              {
                sensitivity: "base",
              }
            )
          );
        setZonesList(zonesDict);
      } catch (error) {
        console.error("Error loading zones:", error);
        setZonesList([]);
      }
    };

    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetZoneBranchId]);

  useEffect(() => {
    loadModulesForBranch(targetZoneBranchId || user?.active_branch_id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetZoneBranchId, user?.active_branch_id]);

  useEffect(() => {
    if (selectedOption === "new_event") {
      loadPreviousEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, eventsFilterStart, eventsFilterEnd]);

  const tutorialColletctionRef = {
    selectMode: useRef(null),
    selectZone: useRef(null),
    includeCollecitionButton: useRef(null),
  };

  const steps = [
    {
      title: "13º passo: Clique no seletor para abrir o menu",
      description:
        "Clique no seletor e selecione 'Gerenciar zonas e categorias'",
      nextButtonProps: {
        children: "OK",
        onClick: () => {
          setSelectedOption("add_cameras");
          setIsEighthPhaseTutorialOpen(true);
        },
      },
      target: () => tutorialColletctionRef.selectMode.current,
    },
    {
      title: "14º passo: Clique no seletor para configurar as zonas",
      description: "Escolhe em qual zona você deseja cadastrar as categorias",
      nextButtonProps: {
        children: "OK",
        onClick: () => {
          setIsEighthPhaseTutorialOpen(false);
          setIsNinthPhaseTutorialOpen(true);
        },
      },
      prevButtonProps: {
        children: "Anterior",
      },
      target: () => tutorialColletctionRef.selectZone.current,
    },
    {
      title: "15º passo: Clique no botão para incluir uma nova categoria",
      description:
        "Clique no botão e cadastre o nome e a data de inicio de relação entre a zona e a categoria",
      nextButtonProps: {
        children: "OK",
      },
      target: () => tutorialColletctionRef.includeCollecitionButton.current,
    },
  ];

  const zoneCollectionIdField = isOuranZoneMode
    ? "zone_ouran_category_id"
    : "zonecollection_id";
  const validZoneCollections = zoneCollection.filter(
    (item) => item[zoneCollectionIdField]
  );
  const activeZoneCollections = validZoneCollections.filter(
    (item) => !item.union_end_datetime
  );
  const inactiveZoneCollections = validZoneCollections.filter(
    (item) => item.union_end_datetime
  );

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          components: {
            Card: {
              headerBg: "var(--ouran-card-header-bg)",
            },
          },
        }}>
        <Flex
          style={{ width: "100%", margin: "0 auto" }}
          justify="center"
          align="center"
          vertical
          gap={20}>
          <Card
            title="Registre eventos e edite as suas categorias!"
            style={{
              width: "90%",
              backgroundColor: "var(--ouran-card-header-bg)",
            }}>
            <Row>
              <Col span={24}>
                <Text style={{ fontSize: 16 }} strong>
                  O que você deseja fazer?
                </Text>
              </Col>
            </Row>
          </Card>

          <Row>
            <Col span={16}>
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  flexWrap: "nowrap",
                  justifyContent: "flex-start",
                  width: "100%",
                }}>
                {[
                  ...BASE_CARD_OPTIONS,
                  ...(isPrivileged ? SUPERADMIN_CARD_OPTIONS : []),
                ].map(({ value, label, icon }) => (
                  <Button
                    key={value}
                    type={selectedOption === value ? "primary" : "default"}
                    onClick={() => {
                      setSelectedOption(value);
                      cleanUsesState();
                      setIsSeventhPhaseTutorialOpen(false);
                      setIsEighthPhaseTutorialOpen(true);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px 12px",
                      textAlign: "center",
                      height: 70,
                    }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>

                    <div
                      style={{
                        fontSize: 14,
                        whiteSpace: "nowrap",
                      }}>
                      {label}
                    </div>
                  </Button>
                ))}
              </div>
            </Col>
          </Row>

          {selectedOption === "new_event" ? (
            <>
              <Card
                title={
                  editingEventGroupId
                    ? "Editar evento"
                    : "Registre um novo evento"
                }
                style={{ width: "85%" }}>
                <Row
                  gutter={[16, 16]}
                  style={{ display: "flex", alignItems: "center" }}>
                  {/* Empresa + Filiais selectors — create mode and multi-branch edit, privileged only */}
                  {(!editingEventGroupId || editingIsMultiBranch) &&
                    isPrivileged && (
                      <>
                        <Col span={4}>
                          <Text strong>Empresa: </Text>
                        </Col>
                        <Col span={20}>
                          <Select
                            options={availableClients}
                            style={{ width: "100%" }}
                            value={selectedCompany}
                            onChange={handleCompanyChange}
                            placeholder="Selecione a empresa"
                          />
                        </Col>

                        <Col span={4}>
                          <Text strong>Filiais: </Text>
                        </Col>
                        <Col span={20}>
                          <Select
                            mode="multiple"
                            options={availableBranches}
                            style={{ width: "100%" }}
                            onChange={setSelectedBranches}
                            disabled={!selectedCompany}
                            placeholder={
                              selectedCompany
                                ? "Filial ativa (padrão)"
                                : "Selecione a empresa primeiro"
                            }
                            dropdownRender={(menu) => (
                              <>
                                <div style={{ padding: "8px 12px" }}>
                                  <Button
                                    type="text"
                                    size="small"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={selectAllBranches}>
                                    {availableBranches.length > 0 &&
                                    availableBranches.every((b) =>
                                      selectedBranches.includes(b.value)
                                    )
                                      ? "Desmarcar todas"
                                      : "Marcar todas"}
                                  </Button>
                                </div>
                                <Divider style={{ margin: 0 }} />
                                {menu}
                              </>
                            )}
                            value={selectedBranches}
                          />
                        </Col>
                      </>
                    )}

                  {escopoPorProdutoPermitido && (
                    <EscopoDoEvento
                      valor={escopoDoEvento}
                      onChange={setEscopoDoEvento}
                      tipoTravado={Boolean(editingEventGroupId)}
                      somenteLeitura={escopoTravado}
                      resumo={resumoDoEscopo(editingEvento)}
                      motivoDaLeitura={
                        editandoEventoDoErp
                          ? "Os SKUs vêm do ERP e a sincronização os mantém; aqui só o título e o comentário mudam."
                          : "Este escopo não é editável por aqui."
                      }
                    />
                  )}

                  {/* Zone selector */}
                  {tipoDoEscopo === "zona" && (
                    <>
                      <Col span={4}>
                        <Text strong>Zona afetada: </Text>
                      </Col>
                      <Col span={20}>
                        {selectedBranches.length > 1 ? (
                          <Text
                            type="secondary"
                            style={{ fontStyle: "italic" }}>
                            Todas as zonas de cada filial selecionada
                          </Text>
                        ) : (
                          <Select
                            mode="multiple"
                            options={zonesList}
                            style={{ width: "100%" }}
                            onChange={setZoneAffected}
                            disabled={editingIsMultiBranch}
                            placeholder={
                              editingIsMultiBranch
                                ? "Todas as zonas de cada filial"
                                : undefined
                            }
                            dropdownRender={(menu) => (
                              <>
                                <div style={{ padding: "8px 12px" }}>
                                  <Button
                                    type="text"
                                    size="small"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={selectAllEventZones}>
                                    {zonesList.length > 0 &&
                                    zonesList.every((zone) =>
                                      zoneAffected.includes(zone.value)
                                    )
                                      ? "Desmarcar todas"
                                      : "Marcar todas"}
                                  </Button>
                                </div>
                                <Divider style={{ margin: 0 }} />
                                {menu}
                              </>
                            )}
                            value={zoneAffected}
                          />
                        )}
                      </Col>
                    </>
                  )}

                  {editingEventGroupId && (
                    <>
                      <Col span={4}>
                        <Text strong>Editando evento: </Text>
                      </Col>
                      <Col span={20}>
                        <Text type="secondary">
                          #{editingEventGroupId}{" "}
                          {editingIsMultiBranch ? (
                            <Text type="warning">
                              · Todas as zonas · {editingBranchCount} filiais —
                              alterações aplicadas em todas
                            </Text>
                          ) : tipoDoEscopo !== "zona" ? (
                            <>
                              (evento por produto
                              {editandoEventoDoErp ? " importado do ERP" : ""})
                            </>
                          ) : (
                            <>
                              ({editingEventIds.length} zona
                              {editingEventIds.length === 1 ? "" : "s"})
                            </>
                          )}
                        </Text>
                      </Col>
                    </>
                  )}
                  <Col span={24} align="center" style={{ paddingTop: 20 }}>
                    <Text strong>Descrição do evento </Text>
                  </Col>
                  <Col span={4}>
                    <Text strong>Título: </Text>
                  </Col>
                  <Col span={20}>
                    <Input
                      maxLength={100}
                      value={newEventTitle}
                      onChange={(e) =>
                        setNewEventTitle(e.target.value)
                      }></Input>
                  </Col>
                  <Col span={4}>
                    <Text strong>Comentário: </Text>
                  </Col>
                  <Col span={20}>
                    <TextArea
                      rows={4}
                      placeholder="Máximo de 200 caracteres"
                      maxLength={200}
                      value={newEventComment}
                      onChange={(e) =>
                        setNewEventComment(e.target.value)
                      }></TextArea>
                  </Col>
                  <Col span={4}>
                    <Text strong>Data de início: </Text>
                  </Col>
                  <Col span={20}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      disabled={editandoEventoDoErp}
                      value={selectedDate ? dayjs(selectedDate) : datePickerNow}
                      onChange={(date) =>
                        setSelectedDate(date ? date.format("YYYY-MM-DD") : "")
                      }
                    />
                  </Col>
                  <Col span={4}>
                    <Text strong>Data de fim: </Text>
                  </Col>
                  <Col span={20}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      disabled={editandoEventoDoErp}
                      value={newEventEndDate ? dayjs(newEventEndDate) : null}
                      onChange={(date) => setNewEventEndDate(date)}
                      placeholder="Opcional"
                    />
                    {newEventEndDate && !editandoEventoDoErp && (
                      <Button
                        onClick={() => setNewEventEndDate(null)}
                        style={{ marginLeft: 10 }}>
                        Limpar
                      </Button>
                    )}
                  </Col>
                  <Col span={24} align="center">
                    {editingEventGroupId ? (
                      <Row justify="center" gutter={12}>
                        <Col>
                          <Button onClick={cancelEditMode}>Cancelar</Button>
                        </Col>
                        <Col>
                          <Button type="primary" onClick={saveEditEvent}>
                            Salvar alterações
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Button type="primary" onClick={createEvent}>
                        Adicionar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card>

              <Card
                title="Eventos anteriores"
                style={{ width: "85%" }}
                loading={isLoadingEvents}>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col>
                    <Text strong>Filtrar por período: </Text>
                  </Col>
                  <Col>
                    <DatePicker
                      format="DD-MM-YYYY"
                      placeholder="De"
                      value={eventsFilterStart}
                      onChange={(date) => setEventsFilterStart(date)}
                      style={{ marginRight: 8 }}
                    />
                    <DatePicker
                      format="DD-MM-YYYY"
                      placeholder="Até"
                      value={eventsFilterEnd}
                      onChange={(date) => setEventsFilterEnd(date)}
                    />
                  </Col>
                </Row>
                <Table
                  size="small"
                  dataSource={previousEvents}
                  rowKey="id"
                  pagination={{ pageSize: 8 }}
                  columns={[
                    {
                      title: "Data início",
                      dataIndex: "datetime",
                      key: "datetime",
                      render: (val) => dayjs(val).format("DD-MM-YYYY"),
                      width: 140,
                    },
                    {
                      title: "Data fim",
                      dataIndex: "end_datetime",
                      key: "end_datetime",
                      render: (val) =>
                        val ? dayjs(val).format("DD-MM-YYYY") : "-",
                      width: 140,
                    },
                    {
                      title: "Zona",
                      dataIndex: "zone_label",
                      key: "zone_label",
                      width: 280,
                      render: (val) => val || "-",
                    },
                    {
                      title: "Escopo",
                      key: "escopo",
                      width: 200,
                      render: (_, record) => (
                        <Flex gap={4} wrap="wrap">
                          <Tag>{resumoDoEscopo(record)}</Tag>
                          {record.origem === "erp" && (
                            <Tag color="geekblue">ERP</Tag>
                          )}
                        </Flex>
                      ),
                    },
                    {
                      title: "Título",
                      dataIndex: "title",
                      key: "title",
                    },
                    {
                      title: "Comentário",
                      dataIndex: "comment",
                      key: "comment",
                      ellipsis: true,
                    },
                    {
                      title: "Ação",
                      key: "action",
                      width: 150,
                      render: (_, record) => {
                        const canAct =
                          (!record.is_multi_branch &&
                            record.origem !== "erp") ||
                          isPrivileged;
                        const canDelete = canAct && record.origem !== "erp";
                        return (
                          <Flex gap={8}>
                            <Button
                              size="small"
                              type={
                                editingEventGroupId === record.id
                                  ? "primary"
                                  : "default"
                              }
                              disabled={!canAct}
                              onClick={() =>
                                canAct && selectEventForEdit(record)
                              }>
                              Editar
                            </Button>
                            <Popconfirm
                              title="Excluir evento"
                              description={
                                record.is_multi_branch
                                  ? `Esta ação excluirá o evento em ${record.branch_count} filiais. Confirmar?`
                                  : "Tem certeza que deseja excluir este evento?"
                              }
                              okText="Excluir"
                              cancelText="Cancelar"
                              disabled={!canDelete}
                              onConfirm={() =>
                                canDelete &&
                                handleDeleteEvent(record.id, record.event_ids)
                              }>
                              <Button
                                size="small"
                                danger
                                disabled={!canDelete}
                                icon={<DeleteOutlined />}
                                loading={deletingEventId === record.id}
                              />
                            </Popconfirm>
                          </Flex>
                        );
                      },
                    },
                  ]}
                  locale={{ emptyText: "Nenhum evento encontrado" }}
                />
              </Card>
            </>
          ) : selectedOption === "edit_zone_collection" ? (
            <Card
              ref={tutorialColletctionRef.selectZone}
              title="Mude a categoria de uma zona"
              style={{ width: "85%" }}>
              <Row
                gutter={[16, 32]}
                style={{ display: "flex", alignItems: "center" }}>
                <Col span={4}>
                  <Text strong>Selecione a zona:</Text>
                </Col>
                <Col span={20}>
                  <Select
                    options={zonesList}
                    style={{ width: "35%" }}
                    onChange={(e) => {
                      setZoneId(e);
                      setIsEighthPhaseTutorialOpen(false);
                      setIsNinthPhaseTutorialOpen(true);
                      setInactiveCollectionsVisibleCount(
                        ZONE_COLLECTION_HISTORY_PAGE_SIZE
                      );
                    }}
                    value={zoneId}
                  />
                </Col>
              </Row>
              {/* Renderizar card e botões somente se zoneId estiver preenchido */}
              {zoneId && (
                <>
                  <Row
                    gutter={[16, 32]}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Col span={24}>
                      <Text strong>Categorias ativas na zona selecionada:</Text>
                    </Col>
                    <Col span={24}>
                      <Table
                        rowKey={zoneCollectionIdField}
                        columns={[
                          {
                            title: "Categoria",
                            dataIndex: "collection",
                            width: "180px",
                            key: "collection",
                          },
                          {
                            title: "Início da relação",
                            dataIndex: "union_start_datetime",
                            width: "140px",
                            key: "union_start_datetime",
                            sorter: (a, b) =>
                              new Date(a.union_start_datetime) -
                              new Date(b.union_start_datetime),
                            render: (text) =>
                              text && dayjs(text).isValid()
                                ? dayjs(text).format("DD-MM-YYYY")
                                : text || "-",
                          },
                          {
                            title: "Substituir categoria",
                            dataIndex: "replace",
                            key: "replace",
                            width: "150px",
                            render: (text, record) => (
                              <Button
                                style={{ borderColor: "blue" }}
                                icon={
                                  <RetweetOutlined style={{ color: "blue" }} />
                                }
                                onClick={() => {
                                  setShowReplaceModal(true);
                                  setCollectionToReplace(record);
                                  setSelectedNewCollectionForReplace(null);
                                }}></Button>
                            ),
                          },
                          {
                            title: "Encerrar relação",
                            dataIndex: "remove",
                            width: "150px",
                            key: "remove",
                            render: (text, record) => (
                              <Button
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => {
                                  setCollectionToRemove(record);
                                  setSelectedRemoveEndDate(null);
                                  setShowRemoveModal(true);
                                }}></Button>
                            ),
                          },
                          {
                            title: "Ações",
                            key: "actions",
                            width: "120px",
                            render: (_, record) => (
                              <TimelineSegmentActions
                                record={record}
                                idField={zoneCollectionIdField}
                                entityLabel="categoria"
                                onEdit={(id, params) =>
                                  isOuranZoneMode
                                    ? editOuranZoneCategorySegment(id, params)
                                    : patchZoneCollectionSegment(id, params)
                                }
                                onDelete={(id, reason) =>
                                  isOuranZoneMode
                                    ? deleteOuranZoneCategorySegment(id, reason)
                                    : deleteZoneCollectionSegment(id, reason)
                                }
                                onDone={() => getZoneCollection(zoneId)}
                                messageApi={messageApi}
                              />
                            ),
                          },
                        ]}
                        dataSource={activeZoneCollections}
                        pagination={false}
                        locale={{
                          emptyText: (
                            <div style={{ textAlign: "center" }}>
                              <CloseOutlined
                                style={{
                                  fontSize: "24px",
                                  marginTop: 10,
                                }}></CloseOutlined>
                              <p>
                                Zona não possui nenhuma categoria ativa no
                                momento
                              </p>
                            </div>
                          ),
                        }}
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        ref={tutorialColletctionRef.includeCollecitionButton}
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setIsAddingCollection(!isAddingCollection);
                          setIsNinthPhaseTutorialOpen(false);
                        }}>
                        Incluir uma nova categoria
                      </Button>
                    </Col>
                  </Row>
                  {isAddingCollection && (
                    <Row
                      gutter={[16, 16]}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 20,
                      }}>
                      <Col span={24}>
                        <Text strong>Nome da categoria</Text>
                      </Col>
                      <Col span={24}>
                        <Select
                          showSearch
                          placeholder="Selecione uma categoria"
                          value={selectedCollection?.value || null}
                          onChange={(value) => {
                            const collection = collections.find(
                              (col) => col.value === value
                            );
                            setSelectedCollection(collection);
                          }}
                          options={collections.map((collection) => ({
                            value: collection.value,
                            label: collection.label,
                          }))}
                          style={{ width: "18%" }}
                          optionFilterProp="label"
                        />
                      </Col>

                      <Col span={24} style={{ marginTop: 10 }}>
                        <Text strong>Data de início da relação</Text>
                      </Col>
                      <Col span={24}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          style={{ width: "18%", marginRight: "10px" }}
                          value={
                            newCollectionStartDate
                              ? dayjs(newCollectionStartDate)
                              : null
                          }
                          onChange={(date) => {
                            setNewCollectionStartDate(
                              date ? date.startOf("day") : null
                            );
                          }}></DatePicker>
                        <Button
                          onClick={() => {
                            setNewCollectionStartDate(dayjs().startOf("day"));
                          }}>
                          Hoje
                        </Button>
                      </Col>

                      <Col span={24} align="left" style={{ marginTop: 10 }}>
                        <Button
                          type="primary"
                          onClick={registerCollectionAndZone}
                          loading={isSubmitting}
                          disabled={
                            isSubmitting ||
                            !selectedCollection ||
                            !newCollectionStartDate
                          }>
                          Adicionar
                        </Button>
                      </Col>
                    </Row>
                  )}
                  <Row
                    gutter={[16, 32]}
                    style={{
                      marginTop: 32,
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Col span={24}>
                      <Text strong>Histórico de categorias encerradas:</Text>
                    </Col>
                    <Col span={24}>
                      <Table
                        rowKey={zoneCollectionIdField}
                        columns={[
                          {
                            title: "Categoria",
                            dataIndex: "collection",
                            width: "180px",
                            key: "collection",
                          },
                          {
                            title: "Início da relação",
                            dataIndex: "union_start_datetime",
                            width: "140px",
                            key: "union_start_datetime",
                            sorter: (a, b) =>
                              new Date(a.union_start_datetime) -
                              new Date(b.union_start_datetime),
                            render: (text) =>
                              text && dayjs(text).isValid()
                                ? dayjs(text).format("DD-MM-YYYY")
                                : text || "-",
                          },
                          {
                            title: "Fim da relação",
                            dataIndex: "union_end_datetime",
                            width: "140px",
                            key: "union_end_datetime",
                            sorter: (a, b) =>
                              new Date(a.union_end_datetime) -
                              new Date(b.union_end_datetime),
                            render: (text) =>
                              text && dayjs(text).isValid()
                                ? dayjs(text).format("DD-MM-YYYY")
                                : "-",
                          },
                          {
                            title: "Ações",
                            key: "actions",
                            width: "120px",
                            render: (_, record) => (
                              <TimelineSegmentActions
                                record={record}
                                idField={zoneCollectionIdField}
                                entityLabel="categoria"
                                onEdit={(id, params) =>
                                  isOuranZoneMode
                                    ? editOuranZoneCategorySegment(id, params)
                                    : patchZoneCollectionSegment(id, params)
                                }
                                onDelete={(id, reason) =>
                                  isOuranZoneMode
                                    ? deleteOuranZoneCategorySegment(id, reason)
                                    : deleteZoneCollectionSegment(id, reason)
                                }
                                onDone={() => getZoneCollection(zoneId)}
                                messageApi={messageApi}
                              />
                            ),
                          },
                        ]}
                        dataSource={inactiveZoneCollections.slice(
                          0,
                          inactiveCollectionsVisibleCount
                        )}
                        pagination={false}
                        locale={{
                          emptyText: (
                            <div style={{ textAlign: "center" }}>
                              <CloseOutlined
                                style={{
                                  fontSize: "24px",
                                  marginTop: 10,
                                }}></CloseOutlined>
                              <p>Zona não possui categorias encerradas</p>
                            </div>
                          ),
                        }}
                      />
                    </Col>
                    {inactiveCollectionsVisibleCount <
                      inactiveZoneCollections.length && (
                      <Col span={24} style={{ textAlign: "center" }}>
                        <Button
                          type="link"
                          onClick={() =>
                            setInactiveCollectionsVisibleCount(
                              (count) =>
                                count + ZONE_COLLECTION_HISTORY_PAGE_SIZE
                            )
                          }>
                          Ver mais{" "}
                          {Math.min(
                            ZONE_COLLECTION_HISTORY_PAGE_SIZE,
                            inactiveZoneCollections.length -
                              inactiveCollectionsVisibleCount
                          )}{" "}
                          categorias
                        </Button>
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </Card>
          ) : selectedOption === "edit_zone_module" ? (
            <Card title="Mude o módulo de uma zona" style={{ width: "85%" }}>
              <Row
                gutter={[16, 32]}
                style={{ display: "flex", alignItems: "center" }}>
                <Col span={4}>
                  <Text strong>Selecione a zona:</Text>
                </Col>
                <Col span={20}>
                  <Select
                    options={zonesList}
                    style={{ width: "35%" }}
                    onChange={(e) => {
                      setZoneModuleId(e);
                      setIsAddingModule(false);
                      setSelectedModuleId(null);
                      setNewModuleStartDate(null);
                      loadZoneModuleLinks(e);
                    }}
                    value={zoneModuleId || undefined}
                    placeholder="Selecione a zona"
                  />
                </Col>
              </Row>
              {zoneModuleId && (
                <>
                  <Row
                    gutter={[16, 32]}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Col span={16}>
                      <Text strong>Módulos vinculados à zona selecionada:</Text>
                    </Col>
                    <Col span={24}>
                      <Table
                        columns={[
                          {
                            title: "Código do módulo",
                            dataIndex: "module_code",
                            key: "module_code",
                            width: "150px",
                          },
                          {
                            title: "Gôndola",
                            dataIndex: "module_gondola",
                            key: "module_gondola",
                            width: "120px",
                            render: (v) => v || "-",
                          },
                          {
                            title: "Início da relação",
                            dataIndex: "union_start_datetime",
                            key: "union_start_datetime",
                            width: "160px",
                            sorter: (a, b) =>
                              new Date(a.union_start_datetime) -
                              new Date(b.union_start_datetime),
                            render: (text) =>
                              text && dayjs(text).isValid()
                                ? dayjs(text).format("DD-MM-YYYY")
                                : "-",
                          },
                          {
                            title: "Fim da relação",
                            dataIndex: "union_end_datetime",
                            key: "union_end_datetime",
                            width: "160px",
                            sorter: (a, b) => {
                              const aEnd = a.union_end_datetime
                                ? new Date(a.union_end_datetime).getTime()
                                : Infinity;
                              const bEnd = b.union_end_datetime
                                ? new Date(b.union_end_datetime).getTime()
                                : Infinity;
                              return aEnd - bEnd;
                            },
                            render: (text) =>
                              text && dayjs(text).isValid()
                                ? dayjs(text).format("DD-MM-YYYY")
                                : "Ativo",
                          },
                          {
                            title: "Encerrar relação",
                            key: "close",
                            width: "140px",
                            render: (_, record) =>
                              record.union_end_datetime ? null : (
                                <Popconfirm
                                  title="Encerrar vínculo com este módulo?"
                                  onConfirm={() =>
                                    handleCloseModuleLink(record.id)
                                  }
                                  okText="Sim"
                                  cancelText="Não">
                                  <Button danger icon={<CloseOutlined />} />
                                </Popconfirm>
                              ),
                          },
                          {
                            title: "Ações",
                            key: "actions",
                            width: "120px",
                            render: (_, record) => (
                              <TimelineSegmentActions
                                record={record}
                                idField="id"
                                entityLabel="módulo"
                                onEdit={(id, params) =>
                                  patchZonesModuleSegment(id, params)
                                }
                                onDelete={(id, reason) =>
                                  deleteZonesModule(id, reason)
                                }
                                onDone={() => loadZoneModuleLinks(zoneModuleId)}
                                messageApi={messageApi}
                              />
                            ),
                          },
                        ]}
                        dataSource={zoneModuleLinks}
                        rowKey="id"
                        pagination={false}
                        locale={{
                          emptyText: (
                            <div style={{ textAlign: "center" }}>
                              <CloseOutlined
                                style={{ fontSize: "24px", marginTop: 10 }}
                              />
                              <p>Zona não possui nenhum módulo vinculado</p>
                            </div>
                          ),
                        }}
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => setIsAddingModule(!isAddingModule)}>
                        Incluir módulo
                      </Button>
                    </Col>
                  </Row>
                  {isAddingModule && (
                    <Row
                      gutter={[16, 16]}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 20,
                      }}>
                      <Col span={24}>
                        <Text strong>Módulo</Text>
                      </Col>
                      <Col span={24}>
                        <Select
                          showSearch
                          loading={isLoadingModules}
                          placeholder={
                            modulesList.length === 0
                              ? "Nenhum módulo disponível para esta filial"
                              : "Selecione um módulo"
                          }
                          value={selectedModuleId}
                          onChange={setSelectedModuleId}
                          options={modulesList}
                          style={{ width: "25%" }}
                          optionFilterProp="label"
                          optionRender={(option) => (
                            <div>
                              <div>{option.label}</div>
                              {option.data.categories?.length > 0 && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#888",
                                    marginTop: 1,
                                  }}>
                                  {option.data.categories.join(", ")}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </Col>
                      <Col span={24} style={{ marginTop: 10 }}>
                        <Text strong>Data de início da relação</Text>
                      </Col>
                      <Col span={24}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          style={{ width: "18%", marginRight: "10px" }}
                          value={
                            newModuleStartDate
                              ? dayjs(newModuleStartDate)
                              : null
                          }
                          onChange={(date) =>
                            setNewModuleStartDate(
                              date ? date.startOf("day").toDate() : null
                            )
                          }
                        />
                        <Button
                          onClick={() =>
                            setNewModuleStartDate(
                              dayjs().startOf("day").toDate()
                            )
                          }>
                          Hoje
                        </Button>
                      </Col>
                      <Col span={24} align="left" style={{ marginTop: 10 }}>
                        <Button
                          type="primary"
                          onClick={handleAddModule}
                          disabled={!selectedModuleId || !newModuleStartDate}>
                          Adicionar
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Card>
          ) : selectedOption === "manual_sku_assignment" ? (
            <ManualSkuAssignment />
          ) : (
            <></>
          )}

          {/*Modal para substituir uma categoria*/}

          <Modal
            open={showReplaceModal}
            onCancel={() => {
              setShowReplaceModal(false);
              setSelectedEndDate(null);
            }}
            title="Substituir Categoria"
            footer={null}
            styles={{
              body: {
                minHeight: "25vh",
              },
            }}>
            <p style={{ marginBottom: 4 }}>
              Você está prestes a substituir{" "}
              <Text strong>{collectionToReplace?.collection}</Text> em{" "}
              <Text strong>
                {zonesList.find((zone) => zone.value === zoneId)?.label ||
                  zoneId}
              </Text>
              .
            </p>
            {replaceFirstAllowedDay && (
              <p>
                <Text type="secondary">
                  {`Data de entrada: ${replaceFirstAllowedDay.format("DD/MM/YYYY")}`}
                </Text>
              </p>
            )}

            <Row gutter={[16, 8]} style={{ marginBottom: "20px" }}>
              <Col span={24}>
                <Text strong>Último dia da categoria atual:</Text>
              </Col>
              <Col span={24}>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: "70%", marginRight: "10px" }}
                  value={selectedEndDate ? replaceLastDay : null}
                  onChange={(date) => {
                    setSelectedEndDate(date ? date.startOf("day") : null);
                  }}
                  disabledDate={(date) =>
                    !!replaceFirstAllowedDay &&
                    date.startOf("day").isBefore(replaceFirstAllowedDay)
                  }
                />
                <Button
                  disabled={
                    !!replaceFirstAllowedDay &&
                    dayjs().startOf("day").isBefore(replaceFirstAllowedDay)
                  }
                  onClick={() => {
                    setSelectedEndDate(dayjs().startOf("day"));
                  }}>
                  Hoje
                </Button>
              </Col>
              <Col span={24} style={{ marginTop: 12 }}>
                <Text strong>Nova categoria:</Text>
              </Col>
              <Col span={24}>
                <Select
                  showSearch
                  placeholder="Selecione uma nova categoria"
                  value={selectedNewCollectionForReplace?.value || null}
                  onChange={(value) => {
                    const newCol = collections.find(
                      (col) => col.value === value
                    );
                    setSelectedNewCollectionForReplace(newCol);
                  }}
                  options={collections.map((collection) => ({
                    value: collection.value,
                    label: collection.label,
                  }))}
                  style={{ width: "100%" }}
                  optionFilterProp="label"
                />
              </Col>
            </Row>

            {selectedEndDate && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "8px 12px",
                  background: "var(--ouran-card-header-bg)",
                  borderRadius: 6,
                }}>
                <div>
                  <Text>
                    {`${collectionToReplace?.collection || "A categoria atual"} permanece até ${replaceLastDay.format("DD/MM/YYYY")} às 23:59:59.`}
                  </Text>
                </div>
                <div>
                  <Text>
                    {`${selectedNewCollectionForReplace?.label || "A nova categoria"} começa em ${replaceLastDay.add(1, "day").format("DD/MM/YYYY")} às 00:00.`}
                  </Text>
                </div>
              </div>
            )}

            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => {
                  setShowReplaceModal(false);
                  setSelectedEndDate(null);
                }}
                style={{ marginRight: 10 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                onClick={() =>
                  updateCollection(
                    collectionToReplace,
                    selectedNewCollectionForReplace
                  )
                }
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  !selectedNewCollectionForReplace ||
                  !selectedEndDate
                }>
                Confirmar
              </Button>
            </div>
          </Modal>

          {/*Modal para remover uma categoria*/}

          <Modal
            open={showRemoveModal}
            onCancel={() => {
              setShowRemoveModal(false);
              setSelectedRemoveEndDate(null);
            }}
            title="Remover Categoria"
            footer={null}
            styles={{
              body: {
                height: "20vh",
              },
            }}>
            <p>
              Você está prestes a encerrar a relação entre{" "}
              {zonesList.find((zone) => zone.value === zoneId)?.label || zoneId}{" "}
              e {collectionToRemove?.collection}
            </p>

            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
              <Col span={24}>
                <Text strong>Último dia da relação:</Text>
              </Col>
              <Col span={24}>
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: "70%", marginRight: "10px" }}
                  value={
                    selectedRemoveEndDate ? dayjs(selectedRemoveEndDate) : null
                  }
                  onChange={(date) => {
                    setSelectedRemoveEndDate(date ? date.endOf("day") : null);
                  }}
                />
                <Button
                  onClick={() => {
                    setSelectedRemoveEndDate(dayjs().endOf("day"));
                  }}>
                  Hoje
                </Button>
              </Col>
            </Row>

            <div style={{ textAlign: "right", marginTop: "32px" }}>
              <Button
                onClick={() => {
                  setShowRemoveModal(false);
                  setSelectedRemoveEndDate(null);
                }}
                style={{ marginRight: 10 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  removeCollection(
                    collectionToRemove,
                    selectedRemoveEndDate
                  ).then(() => {
                    setShowRemoveModal(false);
                    setSelectedRemoveEndDate(null);
                  });
                }}
                disabled={!selectedRemoveEndDate}>
                Confirmar
              </Button>
            </div>
          </Modal>
        </Flex>
        <Tour
          open={shouldShowTutorialBool && isSeventhPhaseTutorialOpen}
          steps={steps.slice(0, 1)}
          onClose={() => {
            setIsSeventhPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Tour
          open={
            shouldShowTutorialBool &&
            isEighthPhaseTutorialOpen &&
            selectedOption === "edit_zone_collection"
          }
          steps={steps.slice(1, 2)}
          onClose={() => {
            setIsEighthPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Tour
          open={shouldShowTutorialBool && isNinthPhaseTutorialOpen}
          steps={steps.slice(2, 3)}
          onClose={() => {
            setIsNinthPhaseTutorialOpen(false);
          }}
          gap={{
            offset: [20, 10],
            radius: 10,
          }}
        />
        <Modal
          title="Tutorial Concluído!"
          open={isFinalTutorialModalOpen && shouldShowTutorialBool}
          onOk={handleFinalTutorial}
          okText="Finalizar Tutorial"
          cancelText="Reiniciar tutorial"
          onCancel={() => navigate("/")}>
          <p style={{ fontSize: 40, textAlign: "center", margin: "1rem" }}>
            &#9989;
          </p>
          <p>
            Parabéns, você concluiu todo o tutorial! Agora, pode utilizar a
            plataforma de forma completa. Explore dados de entrada da loja,
            informações de faturamento e muito mais. Além disso, você também
            pode cadastrar eventos sempre que houver alterações nas categorias.
            Sinta-se à vontade para explorar todas as funcionalidades da
            plataforma!
          </p>
        </Modal>
      </ConfigProvider>
    </>
  );
}
